// Shared quality tiers for model metrics, so every block colours the same
// number the same way (see PillTag for the tone -> token mapping).

// MAPE is a percent (23.8 === 23.8%) and lower is better.
export const mapeTone = (pct) => {
  if (pct == null || !isFinite(pct)) return 'neutral';
  if (pct < 15) return 'green';
  if (pct < 30) return 'warn';
  return 'error';
};

// Human label for the same tiers.
export const mapeLabel = (pct) => ({ green: 'strong', warn: 'fair', error: 'weak' }[mapeTone(pct)] || '');

export const MAPE_TOOLTIP = 'MAPE (Mean Absolute Percentage Error): we re-forecast the last few '
  + 'periods we already know the answer to and compare against the real values. '
  + 'Lower is better — e.g. 8% means predictions were off by about 8% on average.';

// A raw MAE carries no universal good/bad threshold, so it is tiered by MASE
// instead: the MAE divided by the error a naive "nothing changes" forecast
// would have made on the same history. Below 1 beats that baseline, at or
// above 1 does not. Null (flat series, zero naive error) stays neutral.
export const maseTone = (mase) => {
  if (mase == null || !isFinite(mase)) return 'neutral';
  if (mase < 0.9) return 'green';
  if (mase < 1) return 'warn';
  return 'error';
};

// Finer-grained than maseTone: green covers two shades ("strong" well under
// the naive baseline, "good" still comfortably under it).
export const maseLabel = (mase) => {
  if (mase == null || !isFinite(mase)) return '';
  if (mase < 0.5) return 'strong';
  if (mase < 0.9) return 'good';
  if (mase < 1) return 'fair';
  return 'weak';
};

const MAE_TOOLTIP_BASE = 'MAE (Mean Absolute Error): we re-forecast the last few periods we '
  + 'already know the answer to and compare against the real values, this time in the series’ '
  + 'own units rather than a percentage (chosen automatically when actuals are close to zero, '
  + 'where a percentage error would be misleading). Lower is better.';

// Adaptive-precision formatter for values with no fixed scale (e.g. MAE, which
// can range from thousands down to a few thousandths depending on the
// target's units). A fixed decimal count — fine for MAPE's 0-100+ percent
// range — silently rounds small MAE values to "0.00", making a real, non-zero
// error look like a perfect forecast. This keeps ~2-3 significant digits
// regardless of magnitude instead.
export const formatMetricValue = (v) => {
  if (v == null || !isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs === 0) return '0';
  if (abs >= 100) return v.toFixed(1);
  if (abs >= 1) return v.toFixed(2);
  if (abs >= 0.01) return v.toFixed(3);
  const decimals = Math.min(8, Math.ceil(-Math.log10(abs)) + 1);
  return v.toFixed(decimals);
};

// --- Shared metric vocabulary ------------------------------------------------
// Every block that renders a training metric reads its scale, direction, label
// and tooltip from here. Before this existed, three formatters each hardcoded
// their own list and the fallback was "assume a 0–1 ratio and multiply by 100" —
// which turned an RMSE of 12.4 into "1240.0%". That fallback is now impossible:
// an unknown metric is treated as an absolute score, never rescaled.
//
// scale:
//   'ratio'    0–1, render as a percentage (accuracy, ROC AUC, F1, R²)
//   'percent'  already a percentage (MAPE 23.8 === 23.8%)
//   'absolute' the target's own units or an unbounded score (RMSE, MAE, log loss,
//              QWK) — never rescaled, formatted with adaptive precision
//
// Only 'ratio' and 'percent' metrics get a quality tier. An absolute error carries
// no universal good/bad threshold (the same reasoning as the MASE note above), so
// those stay neutral rather than inventing a cutoff.
const RATIO_TOOLTIP_AUC = 'Area under the ROC curve — probability the model ranks a random positive '
  + 'higher than a random negative. 0.5 is random, 1.0 is perfect.';

export const METRIC_META = {
  // --- classification ---
  accuracy: {
    label: 'accuracy', scale: 'ratio', higherIsBetter: true,
    tooltip: 'Share of test rows where the model picked the correct class. Higher is better; chance '
      + 'level depends on the number of classes and any imbalance.',
  },
  balanced_accuracy: {
    label: 'balanced accuracy', scale: 'ratio', higherIsBetter: true,
    tooltip: 'Accuracy averaged per class, so a rare class counts as much as a common one. Higher is better.',
  },
  roc_auc: { label: 'ROC AUC', scale: 'ratio', higherIsBetter: true, tooltip: RATIO_TOOLTIP_AUC },
  auc: { label: 'AUC', scale: 'ratio', higherIsBetter: true, tooltip: RATIO_TOOLTIP_AUC },
  f1_micro: {
    label: 'F1 (micro)', scale: 'ratio', higherIsBetter: true,
    tooltip: 'Harmonic mean of precision and recall, pooled over all classes so every row counts equally. '
      + 'Higher is better.',
  },
  f1: {
    label: 'F1', scale: 'ratio', higherIsBetter: true,
    tooltip: 'Harmonic mean of precision and recall. Robust to class imbalance; higher is better.',
  },
  r2: {
    label: 'R²', scale: 'ratio', higherIsBetter: true,
    tooltip: 'Coefficient of determination — fraction of the variance in the target explained by the '
      + 'model. 1.0 is perfect, 0 is no better than predicting the mean.',
  },
  log_loss: {
    label: 'log loss', scale: 'absolute', higherIsBetter: false,
    tooltip: 'How far the predicted probabilities sit from the truth, penalising confident mistakes '
      + 'hardest. Lower is better; 0 would be perfect.',
  },
  qwk: {
    // Ranges -1..1 rather than 0..1, so it is deliberately NOT a 'ratio': showing
    // 0.82 as "82%" would imply a share of something.
    label: 'QWK', scale: 'absolute', higherIsBetter: true,
    tooltip: 'Quadratic weighted kappa — agreement on ordered categories, where being off by two '
      + 'grades counts far worse than being off by one. 0 is chance, 1.0 is perfect.',
  },

  // --- regression ---
  mape: {
    label: 'MAPE', scale: 'percent', higherIsBetter: false,
    tooltip: 'Mean Absolute Percentage Error — average % difference between predicted and actual '
      + 'values across the test set. Lower is better; values under 10% are typically excellent.',
  },
  smape: {
    label: 'sMAPE', scale: 'percent', higherIsBetter: false,
    tooltip: 'Symmetric Mean Absolute Percentage Error — like MAPE, but stays meaningful when actual '
      + 'values reach zero. Lower is better.',
  },
  rmse: {
    label: 'RMSE', scale: 'absolute', higherIsBetter: false,
    tooltip: 'Root Mean Squared Error — like MAE but penalises large mistakes more heavily, in the '
      + 'target\'s units. Lower is better.',
  },
  rmsle: {
    label: 'RMSLE', scale: 'absolute', higherIsBetter: false,
    tooltip: 'Root Mean Squared Log Error — measures relative rather than absolute error, so a miss '
      + 'on a large value costs no more than the same proportional miss on a small one. Lower is better.',
  },
  mae: {
    label: 'MAE', scale: 'absolute', higherIsBetter: false,
    tooltip: 'Mean Absolute Error — average absolute distance between predicted and actual values, '
      + 'in the target\'s units. Lower is better.',
  },
  median_ae: {
    label: 'median AE', scale: 'absolute', higherIsBetter: false,
    tooltip: 'Median Absolute Error — the typical miss, in the target\'s units. Unlike MAE a handful '
      + 'of extreme outliers cannot dominate it. Lower is better.',
  },
};

// An unrecognised metric is shown as-is rather than guessed at: no rescaling, no
// tier, name used verbatim as the label.
const metricFallback = (name) => ({
  label: name || 'score', scale: 'absolute', higherIsBetter: true, tooltip: undefined,
});

export const metricMeta = (name) => METRIC_META[(name || '').toLowerCase()] || metricFallback(name);

// Number + unit for a metric, without its name. Percent-scale metrics keep two
// decimals (a MAPE of 9.10% and 9.14% are meaningfully different), ratios one.
export const formatMetricNumber = (value, name) => {
  const v = Number(value);
  if (value == null || !isFinite(v)) return '—';
  const { scale } = metricMeta(name);
  if (scale === 'ratio') return `${(v * 100).toFixed(1)}%`;
  if (scale === 'percent') return `${v.toFixed(2)}%`;
  return formatMetricValue(v);
};

// Number + unit + metric name, for places with no separate label (e.g. the model
// comparison cards, where the metric is otherwise never spelled out).
export const formatMetricLabelled = (value, name) => {
  if (value == null || !isFinite(Number(value))) return '—';
  return `${formatMetricNumber(value, name)} ${metricMeta(name).label}`;
};

// Quality tier for any metric, in the PillTag tone vocabulary
// ('green' | 'warn' | 'error' | 'neutral').
export const metricTone = (value, name) => {
  const v = Number(value);
  if (value == null || !isFinite(v)) return 'neutral';
  const { scale, higherIsBetter } = metricMeta(name);
  // Percent-scale error metrics reuse the forecasting tiers so MAPE colours
  // exactly as it always has.
  if (scale === 'percent' && !higherIsBetter) return mapeTone(v);
  if (scale === 'ratio' && higherIsBetter) {
    const pct = v <= 1 ? v * 100 : v;
    if (pct < 50) return 'error';
    if (pct < 75) return 'warn';
    return 'green';
  }
  return 'neutral';
};

// Plain-language verdict for the headline model card ("Good performance"), derived from metricTone
// so the word and the colour of the number beside it can never disagree — one set of cut points
// decides both, and that set is metricTone's (ratio: red < 50%, amber 50–75%, green > 75%; percent
// error: green < 15%, amber 15–30%, red > 30%).
//
// Returns null where metricTone is 'neutral' — the absolute metrics (RMSE, MAE, log loss, QWK,
// median AE). No universal good/bad threshold exists for a score in the target's own units, so the
// card shows the number with no verdict rather than inventing a cutoff.
const GRADE_BY_TONE = {
  green: { label: 'Good', note: null },
  warn: {
    label: 'Acceptable',
    note: 'Depending on the use-case, this is sufficient.',
  },
  error: {
    label: 'Poor',
    note: 'Try adding more data that might help predict the target — more rows and/or columns.',
  },
};

export const metricGrade = (value, name) => {
  const tone = metricTone(value, name);
  const grade = GRADE_BY_TONE[tone];
  return grade ? { ...grade, tone } : null;
};

// Translates the MASE ratio into plain language, so the badge never has to say
// "MASE" out loud.
export const maeTooltip = (mase) => {
  if (mase == null || !isFinite(mase) || mase <= 0) return MAE_TOOLTIP_BASE;
  if (mase >= 1) {
    return `${MAE_TOOLTIP_BASE} This is no more accurate than simply assuming no change from `
      + 'the last known value.';
  }
  return `${MAE_TOOLTIP_BASE} That is about ${(1 / mase).toFixed(1)}× more accurate than simply `
    + 'assuming no change from the last known value.';
};
